import type { EndlessSubmissionMetrics } from "./types";

const STORAGE_KEY = "snake-arena-endless-run-queue-v1";
/** Same size-cap precedent as Campaign's `RunQueue` - a long offline stretch must never grow this without bound. When full, the oldest queued run is dropped to make room for the newest. */
const MAX_QUEUE_SIZE = 30;

function readQueue(): EndlessSubmissionMetrics[] {
  try {
    const raw = globalThis.localStorage?.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function writeQueue(queue: EndlessSubmissionMetrics[]): void {
  try {
    globalThis.localStorage?.setItem(STORAGE_KEY, JSON.stringify(queue));
  } catch {
    // Ignore storage failures - the run itself already happened and the
    // local personal best was already saved via EndlessStorage; losing the
    // queued copy only means this one run's online submission never reaches
    // the server.
  }
}

/** Same rules population and platform - the key `endless_best_runs` keeps one best per (PR34: `platform` is part of it). */
function sameLeaderboardSlot(a: EndlessSubmissionMetrics, b: EndlessSubmissionMetrics): boolean {
  return a.endlessRulesVersion === b.endlessRulesVersion && a.platform === b.platform;
}

/** `candidate` would replace `current` server-side (`record_endless_result()`'s `(length, score) >` comparison). */
function ranksHigher(candidate: EndlessSubmissionMetrics, current: EndlessSubmissionMetrics): boolean {
  if (candidate.finalLength !== current.finalLength) return candidate.finalLength > current.finalLength;
  return candidate.finalScore > current.finalScore;
}

/** What a flush attempt decides about one queued run, so the queue never has to guess whether a failure was transient. Mirrors `RunQueue.FlushOutcome` exactly. */
export type EndlessFlushOutcome = "sent" | "retry-later" | "drop";

/** The minimum shape a flush attempt's result must carry - `flush()` is generic over this so a caller (e.g. `EndlessLeaderboardService`) can attach extra response data (e.g. `isNewBest`/`rank`) without `EndlessQueue` itself needing to know the server's response shape. */
export interface EndlessFlushAttempt {
  outcome: EndlessFlushOutcome;
}

/**
 * A small, Endless-specific offline queue - deliberately its own module
 * (own `localStorage` key, own type), never a generalization of Campaign's
 * `RunQueue` into something Campaign-shaped-and-Endless-shaped at once.
 * Same proven behavior: local, size-bounded, `runId`-deduplicated (a retry
 * of an already-queued run never double-enqueues), never touches
 * `EndlessStorage` (the local personal best is saved independently and
 * unconditionally, before this queue is ever touched - see
 * `GameScene.finishEndlessRun()`).
 */
export const EndlessQueue = {
  list(): readonly EndlessSubmissionMetrics[] {
    return readQueue();
  },

  size(): number {
    return readQueue().length;
  },

  enqueue(run: EndlessSubmissionMetrics): void {
    const queue = readQueue();
    if (queue.some((entry) => entry.runId === run.runId)) return;
    queue.push(run);
    while (queue.length > MAX_QUEUE_SIZE) queue.shift();
    writeQueue(queue);
  },

  /**
   * PR34: local-only capture of a finished Endless run while Online
   * Features are off - the exact counterpart of Campaign's
   * `RunQueue.captureWhileOffline()`. Nothing is ever sent from here; the
   * queue only flushes through the opted-in `EndlessLeaderboardService`.
   * Keeps at most ONE captured run per leaderboard slot (see
   * `sameLeaderboardSlot()`), replaced only by a run that would actually
   * win `record_endless_result()`'s own monotonic comparison (length desc,
   * then score desc; an exact tie keeps the earlier run) - so the FIFO cap
   * can never evict the player's best in favor of later, worse runs.
   */
  captureWhileOffline(run: EndlessSubmissionMetrics): void {
    const queue = readQueue();
    if (queue.some((entry) => entry.runId === run.runId)) return;
    const rivals = queue.filter((entry) => sameLeaderboardSlot(entry, run));
    if (rivals.some((entry) => !ranksHigher(run, entry))) return;
    const rivalIds = new Set(rivals.map((entry) => entry.runId));
    const next = queue.filter((entry) => !rivalIds.has(entry.runId));
    next.push(run);
    while (next.length > MAX_QUEUE_SIZE) next.shift();
    writeQueue(next);
  },

  /**
   * Attempts to send every queued run, in order (oldest first). Stops at
   * the first `"retry-later"` outcome but still applies every `"sent"`/
   * `"drop"` decision made before that point - same semantics as
   * `RunQueue.flush()`. Generic over the submit callback's result type (as
   * long as it carries an `outcome`) so a caller can thread extra response
   * data (e.g. `isNewBest`/`rank`) through to its own return value, without
   * `EndlessQueue` itself needing to know anything about the server's
   * response shape.
   */
  async flush<R extends EndlessFlushAttempt>(submit: (run: EndlessSubmissionMetrics) => Promise<R>): Promise<Map<string, R>> {
    const queue = readQueue();
    const outcomes = new Map<string, R>();
    if (queue.length === 0) return outcomes;

    for (const run of queue) {
      const result = await submit(run);
      outcomes.set(run.runId, result);
      if (result.outcome === "retry-later") break;
      // "sent" and "drop" both remove the entry - only the reason differs.
    }
    // PR34: same merge-safe write as `RunQueue.flush()` - remove only what
    // THIS flush decided, never a run enqueued while it was awaiting.
    const decided = new Set([...outcomes].filter(([, result]) => result.outcome !== "retry-later").map(([runId]) => runId));
    writeQueue(readQueue().filter((run) => !decided.has(run.runId)));
    return outcomes;
  },
};
